<template>
    <div class="tuev-sued-status loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>

        <template v-if="!isLoading">
            <div class="tuev-sued-status__header">
                <h1 class="tuev-sued-status__title">
                    TÜV SÜD Systemstatus SAP Integration
                </h1>
                <b-button variant="primary" @click="loadData">
                    <i aria-hidden="true" class="inticon-refresh"/>
                    Aktualisieren
                </b-button>
            </div>

            <!-- Overall traffic-light card -->
            <div class="card-wrapper mb-3">
                <b-card class="h-100">
                    <b-card-text>
                        <div class="tuev-sued-status__overall">
                            <csp-tuev-sued-status-indicator :color="overall" size="lg"/>
                            <div class="tuev-sued-status__overall-text">
                                <span class="tuev-sued-status__overall-label">{{ overallLabel }}</span>
                                <span v-if="overallCausingCards.length > 0" class="tuev-sued-status__overall-summary">
                                    {{ overallCausingCardsSummary }}
                                </span>
                            </div>
                        </div>
                    </b-card-text>
                </b-card>
            </div>

            <!-- Traffic-light cards -->
            <b-row>
                <b-col v-for="card in statusCards" :key="card.key" class="mb-3" cols="4">
                    <div class="card-wrapper tuev-sued-status__status-card h-100">
                        <b-card class="h-100">
                            <template v-slot:header>
                                <csp-tuev-sued-status-indicator :color="card.color" size="sm"/>
                                <span class="ml-2">{{ card.label }}</span>
                            </template>
                            <b-card-text>
                                <i :class="`inticon inticon-${card.icon}`"/>

                                <div v-if="!card.hasValue2" class="card-inner-row">
                                </div>

                                <div class="card-inner-row">
                                    <div class="column">
                                        <span class="bold">{{ card.value }}</span>
                                        <p>{{ card.valueDescription }}</p>
                                    </div>
                                </div>

                                <div v-if="card.hasValue2" class="card-inner-row">
                                    <div class="column">
                                        <span class="bold">{{ card.value2 }}</span>
                                        <p>{{ card.value2Description }}</p>
                                    </div>
                                </div>
                            </b-card-text>
                        </b-card>
                    </div>
                </b-col>
                <!-- Info card (no traffic light) -->
                <b-col cols="8">
                    <div class="card-wrapper tuev-sued-status__info-card">
                        <b-card header="Letzte Aktualisierung">
                            <b-card-text>
                                <i class="inticon inticon-autoclaim_info"/>
                                <div class="card-inner-row">
                                    <div class="column">
                                        <span class="bold">
                                            {{ formatDateTime(info.lastUpdatedExportDate) || '–' }}
                                        </span>
                                        <p>Laufdatum und -zeit des zuletzt aktualisierten Postens</p>
                                    </div>
                                    <div class="column">
                                    </div>
                                </div>
                                <div class="card-inner-row">
                                    <div class="column">
                                        <span class="bold">{{ formatDateTime(info.lastUpdatedDate) || '–' }}</span>
                                        <p>Aktualisierungszeitpunkt des zuletzt aktualisierten Postens</p>
                                    </div>

                                    <div class="column">
                                        <span class="bold">{{ formatNumber(info.updatedLast24h) }}</span>
                                        <p>Anzahl aktualisierter Posten in den letzten 24h</p>
                                    </div>
                                </div>
                            </b-card-text>
                        </b-card>
                    </div>
                </b-col>
            </b-row>
        </template>
    </div>
</template>

<script>
    import {formatDateTime} from '@js/functions/date';
    import {formatInteger} from '@js/functions/format';
    import Container from '@services/Container';
    import StatusIndicator from './StatusIndicator.vue';
    import '@desktop/scss/components/tuev-sued-status.scss';

    const CARD_CONFIG = {
        failedMessages: {
            icon: 'autoclaim_attention',
            label: 'Fehlgeschlagene Posten',
            valueDescription: 'Anzahl von Posten, deren Verarbeitung fehlgeschlagen ist',
            value2Description: null,
        },
        lineItemsWithoutAppraisal: {
            icon: 'autoclaim_legend',
            label: 'Posten ohne Forderung',
            valueDescription: 'Posten, die keiner Forderung zugeordnet werden konnten',
            value2Description: null,
        },
        openMessages: {
            icon: 'reload',
            label: 'Posten in Verarbeitung',
            valueDescription: 'Ältester Posten, der sich in der Verarbeitung befindet',
            value2Description: 'Anzahl von Posten, die sich in der Verarbeitung befinden',
        },
        reception: {
            icon: 'autoclaim_mileage',
            label: 'Empfang von Anfragen',
            valueDescription: 'Zuletzt erfolgreich empfangener Request',
            value2Description: 'Zuletzt abgelehnter Request',
        },
    };

    const OVERALL_LABELS = {
        green: 'Alles in Ordnung',
        red: 'Handlungsbedarf',
        yellow: 'Achtung',
    };

    export default {
        name: 'TuevSuedStatus',
        components: {
            'csp-tuev-sued-status-indicator': StatusIndicator,
        },
        data() {
            return {
                info: {},
                isLoading: true,
                overall: 'green',
                statusCards: [],
            };
        },
        computed: {
            overallCausingCards() {
                if (this.overall === 'green') {
                    return [];
                }
                return this.statusCards.filter((card) => card.color === this.overall);
            },
            overallCausingCardsSummary() {
                return this.overallCausingCards.map((card) => card.label).join(' · ');
            },
            overallLabel() {
                return OVERALL_LABELS[this.overall] || this.overall;
            },
        },
        created() {
            this.loadData();
        },
        methods: {
            formatDateTime,
            formatNumber(value) {
                return value !== null && value !== undefined ? formatInteger(value) : '–';
            },
            // Maps a raw backend card into the shape the template renders.
            mapCard(card) {
                const config = CARD_CONFIG[card.key] || {};
                const isTimestampValue = card.key === 'reception' || card.key === 'openMessages';
                return {
                    color: card.color,
                    hasValue2: card.hasValue2,
                    icon: config.icon,
                    key: card.key,
                    label: config.label || card.key,
                    value: isTimestampValue ? (this.formatDateTime(card.value) || '–') : this.formatNumber(card.value),
                    value2: card.key ===
                        'openMessages' ? this.formatNumber(card.value2) : (this.formatDateTime(card.value2) || '–'),
                    value2Description: config.value2Description,
                    valueDescription: config.valueDescription,
                };
            },
            loadData() {
                this.isLoading = true;
                Container.getInstance().getDesktopTuevSuedStatusRepository().loadStatus()
                    .then((response) => {
                        const attributes = response.data.attributes;
                        this.overall = attributes.overall || 'green';
                        this.statusCards = (attributes.cards || []).map((card) => this.mapCard(card));
                        this.info = attributes.info || {};
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },
        },
    };
</script>
